// src/components/layout/ClientHeader.tsx
import { useState, useEffect, useRef } from "react"
import { NavLink, useNavigate, useParams } from "react-router-dom"
import { auth, db } from "../../services/firebase"
import { onAuthStateChanged, signOut, User } from "firebase/auth"
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  getDoc,
  serverTimestamp,
  updateDoc,
  doc
} from "firebase/firestore"
import {
  FaBell,
  FaChevronDown,
  FaBars,
  FaTimes
} from "react-icons/fa"
import "./style/ClientHeader.scss"
import { tradeMenu } from "../../constants/tradeMenuConfig"
import { useTranslation } from "react-i18next"
import LanguageCurrencyDropdown from "./LanguageCurrencyDropdown"
import logo from "../../assets/images/logoNeohTrade.png"
import userIcon from "../../assets/images/userIcon.png"
import notificationIcon from "../../assets/images/notificationIcon.png"

type Notification = {
  id: string
  message: string
  timestamp: any
  seen: "Yes" | "No"
}

const publicLinks = [
  { key: "markets", path: "/markets" },
  { key: "accountTypes", path: "/account-types" },
  { key: "news", path: "/news" },
  { key: "earn", path: "/earn" },
  { key: "events", path: "/events" }
]

const ClientHeader = () => {
  const { lng = "en" } = useParams<{ lng: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [menuOpen, setMenuOpen] = useState(false)
  const [tradeOpen, setTradeOpen] = useState(false)
  const [notifOpen, setNotifOpen] = useState(false)
  const [userOpen, setUserOpen] = useState(false)
  const [fullName, setFullName] = useState<string>("")
  const [user, setUser] = useState<User | null>(null)
  const [authChecked, setAuthChecked] = useState(false)
  const [notifications, setNotifications] = useState<Notification[]>([])

  const notifRef = useRef<HTMLDivElement>(null)
  const userRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u)
      setAuthChecked(true)
    })
    return () => unsub()
  }, [])

  useEffect(() => {
    if (!user) {
      setNotifications([])
      return
    }
    const notifCol = collection(db, "users", user.uid, "notifications")
    const q = query(notifCol, orderBy("timestamp", "desc"))
    const unsub = onSnapshot(q, (snap) => {
      const notes: Notification[] = snap.docs.map((d) => {
        const data = d.data() as Omit<Notification, "id">
        return {
          id: d.id,
          message: data.message,
          timestamp: data.timestamp,
          seen: data.seen
        }
      })
      setNotifications(notes)
    })
    return () => unsub()
  }, [user])

  useEffect(() => {
    if (!user) {
      setFullName("")
      return
    }

    const fetchName = async () => {
      const userDoc = await getDoc(doc(db, "users", user.uid))
      if (userDoc.exists()) {
        const data = userDoc.data() as { fullName?: string }
        setFullName(data.fullName || "")
      }
    }

    fetchName()
  }, [user])


  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false)
      }
      if (userRef.current && !userRef.current.contains(e.target as Node)) {
        setUserOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClick)
    return () => document.removeEventListener("mousedown", handleClick)
  }, [])

  const withLang = (path: string) => `/${lng}${path}`

  const handleLogout = async () => {
    await signOut(auth)
    navigate(withLang("/login"))
  }

  const sendTestNotification = async () => {
    if (!user) return
    await addDoc(
      collection(db, "users", user.uid, "notifications"),
      {
        message: t("header.testNotification", {
          time: new Date().toLocaleTimeString(lng)
        }),
        timestamp: serverTimestamp(),
        seen: "No"
      }
    )
  }

  const markAsSeen = async (note: Notification) => {
    if (!user) return
    const noteRef = doc(db, "users", user.uid, "notifications", note.id)
    await updateDoc(noteRef, { seen: "Yes" })
  }

  if (!authChecked) {
    return <header className="client-header placeholder" />
  }

  const unreadCount = notifications.filter((n) => n.seen === "No").length

  return (
    <header className="client-header">
      {/* Logo */}
      <div className="logo" onClick={() => navigate(`/${lng}`)}>
        <img src={logo} alt="Logo"/>
      </div>

      {/* Main Navigation */}
      <nav className={`main-nav ${menuOpen ? "open" : ""}`}>
        <ul className="nav-items">
          {publicLinks.map(({ key, path }) => (
            <li key={key}>
              <NavLink to={withLang(path)}>
                {t(`header.${key}`)}
              </NavLink>
            </li>
          ))}

          <li
            className="has-dropdown"
            onMouseEnter={() => setTradeOpen(true)}
            onMouseLeave={() => setTradeOpen(false)}
          >
            <span className="menu-label">
              {t("header.trade")}{" "}
              <FaChevronDown className={tradeOpen ? "rotated" : ""} />
            </span>

            <div className={`mega-dropdown ${tradeOpen ? "show" : ""}`}>
              <div className="mega-container">
                {tradeMenu.map((item) => (
                  <div
                    key={item.label}
                    className="dropdown-item"
                    onClick={() => navigate(withLang(item.path))}
                  >
                    <div className="icon">{item.icon}</div>
                    <div className="info">
                      <div className="label">{item.label}</div>
                      <p className="description">{item.description}</p>
                    </div>
                    
                  </div>
                ))}
              </div>
            </div>
          </li>
        </ul>
      </nav>

      {/* Right Side Controls */}
      <div className="header-right">
        {/* Hamburger (mobile) */}
        <div className="hamburger" onClick={() => setMenuOpen((o) => !o)}>
          {menuOpen ? <FaTimes /> : <FaBars />}
        </div>

        {/* Notifications */}
        {user && (
          <div className="notif-wrapper" ref={notifRef}>
  <img
    src={notificationIcon}
    alt="Notifications"
    className="notif-icon"
    onClick={() => setNotifOpen((o) => !o)}
    style={{ cursor: "pointer" }}
  />
  {unreadCount > 0 && (
    <span className="notif-badge">{unreadCount}</span>
  )}


            <div className={`notif-dropdown ${notifOpen ? "show" : ""}`}>
              <div className="notif-header">
                {t("header.notifications")}
                
              </div>
              <ul>
                {notifications.length === 0 && (
                  <li className="empty">{t("header.noNotifications")}</li>
                )}
                {notifications.map((note) => (
                  <li
                    key={note.id}
                    className={note.seen === "No" ? "unread" : ""}
                    onClick={() => markAsSeen(note)}
                  >
                    {note.message}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* User Menu or Auth Buttons */}
        {user ? (
          <div className="user-wrapper" ref={userRef}>
            <div
              className={`user-avatar ${userOpen ? "rotated" : ""}`}
              onClick={() => setUserOpen((o) => !o)}
            >
              <img src={userIcon}/> { fullName } <FaChevronDown />
            </div>
            <ul className={`user-dropdown ${userOpen ? "show" : ""}`}>
              <li onClick={() => navigate(withLang("/user/profile"))}>
                {t("header.profile")}
              </li>
              <li onClick={() => navigate(withLang("/user/portfolio"))}>
                {t("header.portfolio")}
              </li>
              <li onClick={() => navigate(withLang("/user/deposit"))}>
                {t("header.deposit")}
              </li>
              <li onClick={() => navigate(withLang("/user/withdraw"))}>
                {t("header.withdraw")}
              </li>
              <li onClick={() => navigate(withLang("/user/notifications"))}>
                {t("header.notifications")}
              </li>
            
              <li onClick={handleLogout}>{t("header.logout")}</li>
            </ul>
          </div>
        ) : (
          <div className="auth-buttons">
            <NavLink to={withLang("/login")}>{t("header.login")}</NavLink>
            <NavLink to={withLang("/register")} className="register">
              {t("header.register")}
            </NavLink>
          </div>
        )}

        {/* Language & Currency */}
        <LanguageCurrencyDropdown />
      </div>
    </header>
  )
}

export default ClientHeader
